/* Grade10MathLesson.js
   -----------------------------------
   A React component that provides:
   - Chapter-based lesson slides for Grade 10 math (BC Curriculum).
   - Chapter quizzes (10 questions each).
   - A final quiz of 50 questions across all topics.

   Mirrors the structure of Grade9MathLesson.js
*/

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import "./Grade10MathLesson.css";
import { chapterPages } from "./Grade10chapterPages";
// Reuse the same tools:
import Calculator from "../MathTools/Calculator";
import WritingPad from "../MathTools/WritingPad";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg";
import { awardToken } from "../../awardtoken";  // <— new import for awarding tokens
import { generateQuizQuestion } from "./Grade10QuizGenerator";


/* ----------------------------------------------------------
   1) Chapter definitions and pages (the "slides" of the lesson)
   We have 9 major topics + final summary (chapter 10).
---------------------------------------------------------- */
const chapterTopicMap = {
  1: "exponentsRadicals",
  2: "polynomialOperations",
  3: "polynomialFactoring",
  4: "relationsFunctions",
  5: "linearFunctions",
  6: "arithmeticSequences",
  7: "systemsLinearEq",
  8: "rightTriangleTrig",
  9: "financialLit",
  10: null // final summary => no single topic
};


/* ----------------------------------------------------------
   Main Grade10MathLesson component
---------------------------------------------------------- */
export default function Grade10MathLesson(onAwardTokens) {
  // 1) Chapter states
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);

  // 2) Chapter quiz states
  const [showChapterQuiz, setShowChapterQuiz] = useState(false);
  const [chapterQuiz, setChapterQuiz] = useState([]);
  const [chQidx, setChQidx] = useState(0);
  const [chScore, setChScore] = useState(0);
  const [chFin, setChFin] = useState(false);

  // 3) Overall progress
  const [allChaptersDone, setAllChaptersDone] = useState(false);
  // NEW: track if user had previously completed everything
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);

  useEffect(() => {
    // Check localStorage for "grade10MathComplete"
    const completedBefore = localStorage.getItem("grade10MathComplete");
    if (completedBefore === "true") {
      setHasCompletedBefore(true);
      setAllChaptersDone(true);
    }
  }, []);

  useEffect(() => {
    // Once they truly finish all chapters, store that in localStorage
    if (allChaptersDone) {
      localStorage.setItem("grade10MathComplete", "true");
    }
  }, [allChaptersDone]);

  // 4) Final quiz states
  const [showFinal, setShowFinal] = useState(false);
  const [finalQuizAttempted, setFinalQuizAttempted] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fDone, setFDone] = useState(false);

  // Keep a ref so we only award tokens once
  const hasAwardedTokens = useRef(false);

  // 5) UI toggles for calculator & writing pad
  const [showCalc, setShowCalc] = useState(false);
  const [showPad, setShowPad] = useState(false);

  // 6) user info
  const [user, setUser] = useState(null);
  useEffect(() => {
    const logged = JSON.parse(localStorage.getItem("loggedInUser"));
    if (logged) setUser(logged);
  }, []);

  // Gather the pages for the current chapter
  const pages = chapterPages[chapter];
  const currentPage = pages[pageIdx];
  const isLastPage = pageIdx === pages.length - 1;

  // total slides across all chapters
  const totalSlides = useMemo(() => {
    return Object.values(chapterPages).reduce((acc, arr) => acc + arr.length, 0);
  }, []);
  // slides completed so far
  const slidesSoFar = useMemo(() => {
    let sum = 0;
    for (let c = 1; c < chapter; c++) {
      sum += chapterPages[c].length;
    }
    sum += pageIdx;
    return sum;
  }, [chapter, pageIdx]);

  const progress = Math.round(((slidesSoFar + 1) / totalSlides) * 100);

  /* ------------------------------------------
     Navigate pages
  ------------------------------------------ */
  function nextPage() {
    if (pageIdx < pages.length - 1) {
      setPageIdx(pageIdx + 1);
    }
  }
  function prevPage() {
    if (pageIdx > 0) {
      setPageIdx(pageIdx - 1);
    }
  }

  /* ------------------------------------------
     Chapter Quiz
  ------------------------------------------ */
  function startChapterQuiz() {
    const topic = chapterTopicMap[chapter];
    if (!topic) {
      // This is the final summary chapter => no single topic
      setAllChaptersDone(true);
      return;
    }
    // Build a 10-question quiz for that topic
    const quizArr = [];
    for (let i=0; i<10; i++){
      quizArr.push(generateQuizQuestion(topic));
    }
    setChapterQuiz(quizArr);
    setChQidx(0);
    setChScore(0);
    setChFin(false);
    setShowChapterQuiz(true);
  }
  function handleChAnswer(opt) {
    if (String(opt) === String(chapterQuiz[chQidx].correctAnswer)) {
      setChScore(s => s+1);
    }
    if (chQidx < chapterQuiz.length-1) {
      setChQidx(i => i+1);
    } else {
      setChFin(true);
    }
  }
  function finishChapterQuiz() {
    // Require >=6/10 to pass
    if (chScore >= 6) {
      if (chapter < 10) {
        setChapter(ch => ch+1);
        setPageIdx(0);
      } else {
        setAllChaptersDone(true);
      }
      setShowChapterQuiz(false);
    } else {
      alert("Score below 60%. Please review this chapter and try again!");
      // optionally re-start the quiz
      startChapterQuiz();
    }
  }

  /* ------------------------------------------
     Final Quiz
  ------------------------------------------ */
  function startFinalQuiz() {
    // 50 questions from all topics
    const used = new Set();
    const finalArr = [];
    while (finalArr.length < 50) {
      const q = generateQuizQuestion();
      const key = q.question + "||" + q.correctAnswer;
      if (!used.has(key) && q.question !== "No questions available") {
        used.add(key);
        finalArr.push(q);
      }
    }
    setFinalQs(finalArr);
    setFIdx(0);
    setFScore(0);
    setFDone(false);
    setShowFinal(true);
    setFinalQuizAttempted(true); // prevent second attempt
  }
  function handleFinalAns(opt) {
    if (String(opt)===String(finalQs[fIdx].correctAnswer)) {
      setFScore(s=>s+1);
    }
    if (fIdx < finalQs.length-1) {
      setFIdx(i=>i+1);
    } else {
      setFDone(true);
    }
  }

  function CompletedMenu() {
    const chapters = [1,2,3,4,5,6,7,8,9,10];

    function handleChapterSelect(ch) {
      if (!ch) return;
      setChapter(ch);
      setPageIdx(0);
      setShowChapterQuiz(false);
    }

    function handleRetakeFinal() {
      startFinalQuiz();
    }

    return (
      <div style={{ textAlign:"center", margin:"2em 0" }}>
        <h2>Welcome Back!</h2>
        <p>You’ve previously completed all Grade 10 Math chapters. 
           Jump to any chapter for review, or retake the final quiz.</p>
        <div style={{ marginBottom:"1em" }}>
          <label style={{ marginRight:8 }}>Jump to Chapter:</label>
          <select onChange={(e)=>handleChapterSelect(Number(e.target.value))}>
            <option value="">--Select--</option>
            {chapters.map(ch=>(<option key={ch} value={ch}>Chapter {ch}</option>))}
          </select>
        </div>

        <button className="button1" onClick={handleRetakeFinal}>
          Retake Final Quiz
        </button>
      </div>
    );
  }

  /* ------------------------------------------
     Sub-UI for Lesson, Chapter Quiz, Final Quiz
  ------------------------------------------ */
  function LessonUI() {
    return (
      <>
        <h2 className="lesson-page-title">{currentPage.title}</h2>
        <div className="lesson-page-content">{currentPage.body}</div>

        <div className="lesson-page-navigation">
          {pageIdx > 0 && (
            <button className="lesson-nav-btn" onClick={prevPage}>
              ◀ Previous
            </button>
          )}
          {pageIdx < pages.length - 1 && (
            <button className="lesson-nav-btn" onClick={nextPage}>
              Next ▶
            </button>
          )}
        </div>

        {/* If last page in the chapter, show quiz button */}
        {isLastPage && (
          <button className="lesson-nav-btn" onClick={startChapterQuiz}>
            Take Chapter {chapter} Quiz
          </button>
        )}

        <br/>
        <Link to="/math" className="grade10-back">← Back to Math Page</Link>
      </>
    );
  }

  function ChapterQuizUI() {
    if (!chapterQuiz[chQidx]) return <p>Loading quiz...</p>;
    if (!chFin) {
      // quiz in progress
      const q = chapterQuiz[chQidx];
      return (
        <div className="quick-quiz-story-mode">
          <div className="quiz-top-panel">
            <div className="teacher-image-container">
              <img
                src="/images/Teacher.png"
                alt="Teacher"
                className="quiz-character-img"
              />
            </div>
            <div className="quiz-question-box">
              <p className="quiz-question-text">
                Q {chQidx + 1}/{chapterQuiz.length}:{" "}
                <span dangerouslySetInnerHTML={{__html:q.question}}/>
              </p>
              {q.visual && <div style={{marginTop:10}}>{q.visual}</div>}
            </div>
          </div>

          <div className="quiz-bottom-panel">
            <div className="quiz-answer-box-left">
              {q.options.map((opt,i)=>(
                <button
                  key={i}
                  className="quiz-answer-btn"
                  onClick={()=>handleChAnswer(opt)}
                  dangerouslySetInnerHTML={{__html: opt}}
                />
              ))}
              <p className="quiz-score-display">
                Score: {chScore}/{chapterQuiz.length}
              </p>
              <button className="button1" onClick={()=>setShowCalc(c=>!c)}>
                {showCalc ? "Hide Calculator" : "Show Calculator"}
              </button>
              <button className="button1" onClick={()=>setShowPad(p=>!p)}>
                {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
              </button>
              {showCalc && <Calculator />}
              {showPad && <WritingPad />}
            </div>

            <div className="student-image-container">
              <img
                src={user?.avatar || defaultProfile}
                alt="Student"
                className="quiz-character-img-student"
              />
            </div>
          </div>
        </div>
      );
    } else {
      // quiz finished
      return (
        <div className="quiz-result-screen">
          <h3>Chapter {chapter} Quiz Finished!</h3>
          <p>You scored {chScore}/{chapterQuiz.length}.</p>
          <button className="quiz-result-btn" onClick={finishChapterQuiz}>
            Continue
          </button>
        </div>
      );
    }
  }

  function FinalQuizUI() {
    if (!finalQs[fIdx]) return <p>Loading final quiz...</p>;
    if (!fDone) {
      const q = finalQs[fIdx];
      return (
        <div className="quick-quiz-story-mode">
          <div className="quiz-top-panel">
            <div className="teacher-image-container">
              <img
                src="/images/teacher.png"
                alt="Teacher"
                className="quiz-character-img"
              />
            </div>
            <div className="quiz-question-box">
              <p className="quiz-question-text">
                Q {fIdx + 1}/{finalQs.length}:{" "}
                <span dangerouslySetInnerHTML={{__html: q.question}}/>
              </p>
              {q.visual && <div style={{ marginTop: 10 }}>{q.visual}</div>}
            </div>
          </div>

          <div className="quiz-bottom-panel">
            <div className="quiz-answer-box-left">
              {q.options.map((opt,i)=>(
                <button
                  key={i}
                  className="quiz-answer-btn"
                  onClick={()=>handleFinalAns(opt)}
                  dangerouslySetInnerHTML={{__html: opt}}
                />
              ))}
              <p className="quiz-score-display">
                Score: {fScore}/{finalQs.length}
              </p>
              <button className="button1" onClick={()=>setShowCalc(c=>!c)}>
                {showCalc ? "Hide Calculator" : "Show Calculator"}
              </button>
              <button className="button1" onClick={()=>setShowPad(p=>!p)}>
                {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
              </button>
              {showCalc && <Calculator />}
              {showPad && <WritingPad />}
            </div>

            <div className="student-image-container">
              <img
                src={user?.avatar || defaultProfile}
                alt="Student"
                className="quiz-character-img-student"
              />
            </div>
          </div>
        </div>
      );
    } else {
      // final quiz finished
 // Possibly award a token if >=80% (like in Grade11)
 if(!hasAwardedTokens.current) {
  const percent = (fScore / finalQs.length)*100;
  if (percent >= 80) {
    awardToken();  // legacy—updates localStorage (if needed by other code)
    if (onAwardTokens) onAwardTokens(1);  // this notifies App.js to increment navbar!
    const updated = JSON.parse(localStorage.getItem("loggedInUser"));
    setUser(updated);
  }
  const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
  quizResults["math_g10"] = {
      ...(quizResults["math_g10"] || {}),
        finalQuizScore: fScore,
          finalQuizTotal: finalQs.length,
            lastCompleted: Date.now()
          };
  localStorage.setItem("quizResults", JSON.stringify(quizResults));
  hasAwardedTokens.current = true;
}

      return (
        <div className="quiz-result-screen">
          <h3>Final Quiz Finished!</h3>
          <p>You scored {fScore}/{finalQs.length}!</p>
          <button className="quiz-result-btn" onClick={()=>setShowFinal(false)}>
            Return to Lesson
          </button>
        </div>
      );
    }
  }

  /* ------------------------------------------
     Render
  ------------------------------------------ */
  return (
    <div className="grade10-math-page">
      <div className="grade10-math-page-scale">
        {/* Progress bar */}
        <div style={{ margin: "10px 0" }}>
          <div
            style={{
              width: "80%",
              margin: "0 auto",
              background: "#ddd",
              borderRadius: 10,
              height: 20
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                background: "#4caf50",
                height: 20,
                transition: "width 0.3s"
              }}
            />
          </div>
          <p>{progress}% complete</p>
        </div>

        {hasCompletedBefore && !showFinal && !showChapterQuiz && (
        <CompletedMenu />
        )}

        {/* If not all chapters done => show lesson or chapter quiz */}
        {!allChaptersDone && !showFinal && (
          <>
            {!showChapterQuiz ? <LessonUI /> : <ChapterQuizUI />}
          </>
        )}

        {/* If done all chapters => let user start final quiz */}
        {allChaptersDone && !showFinal && !finalQuizAttempted &&(
          <div style={{ textAlign: "center" }}>
            <h2>All Chapters Completed!</h2>
            <p>You can now take the final 50-question quiz on all Grade 10 topics.</p>
            <button className="lesson-nav-btn" onClick={startFinalQuiz}>
              Start Final Quiz
            </button>
          </div>
        )}

        {/* Final quiz UI */}
        {showFinal && <FinalQuizUI />}
      </div>
    </div>
  );
}
